<div
  class="textarea-container"
  [attr.data-size]="size"
  [attr.data-disabled]="disabled"
  [attr.data-error]="error"
  [attr.data-has-value]="hasValue"
>
  <label *ngIf="label" class="textarea-label" [for]="inputId">
    {{ label }}
    <span *ngIf="required" class="required-indicator">*</span>
  </label>

  <div class="textarea-wrapper">
    <ng-container *ngIf="richText; else plainTextarea">
      <div class="rich-text-wrapper">
        <div
          class="rich-text-toolbar"
          role="toolbar"
          aria-label="Text formatting"
        >
          <span class="toolbar-button-anchor" umssTooltip="Bold">
            <button
              type="button"
              class="format-button format-button--bold"
              [class.format-button--active]="activeBold"
              aria-label="Bold"
              [disabled]="disabled || readonly"
              (mousedown)="formatRichText($event, 'bold', richTextEditor)"
            >
              B
            </button>
          </span>
          <span class="toolbar-button-anchor" umssTooltip="Italic">
            <button
              type="button"
              class="format-button format-button--italic"
              [class.format-button--active]="activeItalic"
              aria-label="Italic"
              [disabled]="disabled || readonly"
              (mousedown)="formatRichText($event, 'italic', richTextEditor)"
            >
              I
            </button>
          </span>
          <span class="toolbar-button-anchor" umssTooltip="Underline">
            <button
              type="button"
              class="format-button format-button--underline"
              [class.format-button--active]="activeUnderline"
              aria-label="Underline"
              [disabled]="disabled || readonly"
              (mousedown)="formatRichText($event, 'underline', richTextEditor)"
            >
              U
            </button>
          </span>
          <span class="toolbar-divider" aria-hidden="true"></span>
          <span class="toolbar-button-anchor" umssTooltip="Numbered list">
            <button
              type="button"
              class="format-button"
              [class.format-button--active]="activeOrderedList"
              aria-label="Numbered list"
              [disabled]="disabled || readonly"
              (mousedown)="
                formatRichText($event, 'insertOrderedList', richTextEditor)
              "
            >
              1.
            </button>
          </span>
          <span class="toolbar-button-anchor" umssTooltip="Bulleted list">
            <button
              type="button"
              class="format-button"
              [class.format-button--active]="activeUnorderedList"
              aria-label="Bulleted list"
              [disabled]="disabled || readonly"
              (mousedown)="
                formatRichText($event, 'insertUnorderedList', richTextEditor)
              "
            >
              <umss-icon name="phosphorListBulletsBold" size="18px" />
            </button>
          </span>
          <span class="toolbar-divider" aria-hidden="true"></span>
          <span class="toolbar-button-anchor" umssTooltip="Add link">
            <button
              type="button"
              class="format-button"
              [class.format-button--active]="activeLink"
              aria-label="Add link"
              [disabled]="disabled || readonly || !canAddLink"
              (mousedown)="openLinkPopover($event, richTextEditor)"
              (click)="$event.stopPropagation()"
            >
              <umss-icon name="phosphorLink" size="18px" />
            </button>
          </span>
          <umss-popover
            [(open)]="linkPopoverOpen"
            width="240px"
            (closePopover)="onLinkPopoverClosed(richTextEditor)"
          >
            <ng-container *ngIf="linkPopoverMode === 'view'; else linkEditor">
              <div class="link-details">
                <a
                  class="link-details__url"
                  [href]="linkUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ linkUrl }}
                </a>
                <span class="toolbar-button-anchor" umssTooltip="Edit link">
                  <button
                    type="button"
                    class="link-action-button"
                    aria-label="Edit link"
                    (click)="editSelectedLink()"
                  >
                    <umss-icon name="phosphorPencilSimple" size="18px" />
                  </button>
                </span>
                <span class="toolbar-button-anchor" umssTooltip="Remove link">
                  <button
                    type="button"
                    class="link-action-button link-action-button--delete"
                    aria-label="Remove link"
                    (click)="deleteSelectedLink(richTextEditor)"
                  >
                    <umss-icon name="phosphorTrash" size="18px" />
                  </button>
                </span>
              </div>
            </ng-container>
            <ng-template #linkEditor>
              <input
                #linkInput
                type="url"
                class="link-input"
                placeholder="https://example.com"
                aria-label="Link URL"
                [value]="linkUrl"
                (input)="onLinkUrlInput($event)"
                (keydown.enter)="applyRichTextLink($event, richTextEditor)"
              />
            </ng-template>
          </umss-popover>
        </div>
        <div
          #richTextEditor
          [id]="inputId"
          class="textarea-input rich-text-input"
          role="textbox"
          aria-multiline="true"
          [attr.tabindex]="disabled ? -1 : 0"
          [style.min-height.px]="rows * 24 + 16"
          [attr.contenteditable]="disabled || readonly ? 'false' : 'true'"
          [attr.data-placeholder]="placeholder"
          [attr.aria-label]="label"
          [attr.aria-required]="required"
          [attr.aria-invalid]="error"
          (input)="onRichTextInput(richTextEditor)"
          (keydown)="onRichTextKeydown($event, richTextEditor)"
          (paste)="onRichTextPaste($event, richTextEditor)"
          (click)="onRichTextClick($event, richTextEditor)"
          (mouseup)="updateLinkAvailability(richTextEditor)"
          (keyup)="updateLinkAvailability(richTextEditor)"
          (focus)="onTextareaFocus()"
          (blur)="onTextareaBlur()"
        ></div>
      </div>
    </ng-container>

    <ng-template #plainTextarea>
      <textarea
        #textarea
        [id]="inputId"
        class="textarea-input"
        [style.resize]="resize"
        [placeholder]="placeholder"
        [rows]="rows"
        [disabled]="disabled"
        [readonly]="readonly"
        [required]="required"
        [value]="value"
        [attr.aria-label]="label"
        [attr.aria-required]="required"
        [attr.aria-invalid]="error"
        (input)="onTextareaInput($event)"
        (focus)="onTextareaFocus()"
        (blur)="onTextareaBlur()"
      >
      </textarea>
    </ng-template>

    <div *ngIf="maxLength" class="character-count">
      {{ characterCount }}
    </div>
  </div>

  <p *ngIf="helperText && !error" class="helper-text">
    {{ helperText }}
  </p>

  <p *ngIf="error && errorMessage" class="error-message">
    {{ errorMessage }}
  </p>
</div>
